import { captureMessage, withScope } from "@sentry/react";
import axios, { AxiosResponse } from "axios";

import { ensurePublicTokenIsFresh } from "@/services/ensurePublicTokenIsFresh";
import { CompaxOAuth2AccessTokenModel } from "@/services/model";
import {
  accessTokenAtom,
  refreshTokenAtom,
  showsSessionExpiredModalAtom,
  store,
} from "@/utils/atoms";

import { AUTHORIZE_URL, TOKEN_URL } from "./apiUrls";
import { env } from "./environmentHelpers";

interface AuthResponse {
  access_token: string;
  token_type: "bearer";
  refresh_token: string;
  expires_in: number;
  scope: "all";
}

interface AuthErrorResponse {
  error: string;
  error_description: string;
}

export const anonymousLoginStatus = "IS_ANONYMOUS_LOGIN";
export const agentLoginStatus = "IS_AGENT_LOGIN";

// Base auth url used for both the redirect to the login page and the request for tokens after login
const getAuthURL = (baseURL: string) => {
  const url = new URL(baseURL);
  // client_id identifies the client application, as the SSO server has multiple clients
  // we identify as digital republic client here
  url.searchParams.set("client_id", env.clientAppId || "");
  // we want to redirect back to the current page after login
  url.searchParams.set(
    "redirect_uri",
    `${env.isTest ? "https:" : window.location.protocol}//${
      // Calls to window.location.host are not allowed in tests due to DOM restrictions, so we use localhost instead
      env.isTest ? "localhost" : window.location.host
    }${env.publicUrl || "/"}`,
  );
  // our PKCE implementation generates a code_challenge and code_verifier, the method is SHA-256
  url.searchParams.set("code_challenge_method", "S256");

  return url;
};

/**
 * Use code and verifier to request authorization token
 */
export const fetchAuthToken = async (code: string, verifier: string) => {
  if (!code || !verifier) {
    throw Error("'code' and/or 'verifier' empty");
  }

  const url = getAuthURL(TOKEN_URL.toString());
  url.searchParams.set("grant_type", "authorization_code");
  url.searchParams.set("code", code);
  url.searchParams.set("code_verifier", verifier);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  const json = (await response.json()) as AuthResponse | AuthErrorResponse;

  if ("error" in json) {
    throw Error(
      `Fetching auth token returned an error: ${json.error_description}`,
    );
  }

  // Schedule proactive refresh for the new token
  scheduleTokenRefresh();

  return json;
};

/**
 * Generate a code_verifier that will be sent to request tokens
 */
export const generateVerifier = () => {
  const dec2hex = (dec: number) => `0${dec.toString(16)}`.substring(-2);

  const generateRandomString = () => {
    let array = new Uint32Array(56 / 2);
    array = env.isTest ? array : window.crypto.getRandomValues(array);

    return Array.from(array, dec2hex).join("");
  };

  return generateRandomString();
};

/**
 * Generate a code_challenge from the code_verifier that will be sent to request an authorization_code
 */
export const generateChallengeFromVerifier = async (verifier: string) => {
  const sha256 = (plain: string) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);

    return window.crypto.subtle.digest("SHA-256", data);
  };

  const base64urlencode = (a: ArrayBuffer) => {
    const bytes = new Uint8Array(a);
    const len = bytes.byteLength;
    let str = "";

    for (let i = 0; i < len; i++) {
      str += String.fromCharCode(bytes[i]);
    }

    return window
      .btoa(str)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  };

  const hashed = await sha256(verifier);

  return base64urlencode(hashed);
};

// Calculate a login link with a code challenge and verifier
export const getAuthorizeURL = async (lang: string, verifier: string) => {
  const challenge = await generateChallengeFromVerifier(verifier);

  const url = getAuthURL(AUTHORIZE_URL.toString());
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "all");
  url.searchParams.set("user_language", lang);
  url.searchParams.set("code_challenge", challenge);

  return url;
};

// Proactive token refresh via a 1-second interval.
// The interval reads the current token from the store on every tick and decodes
// its exp claim directly, so it is self-contained: it does not depend on
// external state being kept in sync and automatically handles tokens that were
// updated by other code paths (e.g. a reactive 401 refresh) without any
// additional coordination.
let refreshInterval: NodeJS.Timeout | null = null;

// Shared in-flight promise for the token refresh HTTP call.
// Both the proactive interval and the reactive 401 interceptor call
// refreshAuthorization — without this, they can race and each fire a
// separate POST /token request, burning a single-use refresh token.
// Any concurrent caller that arrives while a refresh is already running
// awaits this same promise and then updates its own request headers.
// Cleared in .finally() so a subsequent failure can start a fresh attempt.
let refreshAuthorizationPromise: Promise<void> | null = null;

const REFRESH_THRESHOLD_SECONDS = 30;

/** Decodes the `exp` claim from a JWT. Returns remaining seconds, or null. */
const getTokenExpiresIn = (token: string): number | null => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));

    if (!payload.exp) return null;

    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
};

const scheduleTokenRefresh = () => {
  // Replace any existing interval with a fresh one.
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }

  refreshInterval = setInterval(async () => {
    const token = store.get(accessTokenAtom);
    const currentRefreshToken = store.get(refreshTokenAtom);

    // Skip anonymous/agent sessions — they have their own refresh paths.
    if (
      !token ||
      !currentRefreshToken ||
      currentRefreshToken === anonymousLoginStatus ||
      currentRefreshToken === agentLoginStatus
    ) {
      return;
    }

    const secondsLeft = getTokenExpiresIn(token);

    if (secondsLeft !== null && secondsLeft <= REFRESH_THRESHOLD_SECONDS) {
      // Stop this interval synchronously before awaiting — the interval cannot
      // fire again after clearInterval, so no re-entry guard is needed.
      // If the refresh succeeds, refreshAuthorization calls scheduleTokenRefresh
      // which starts a fresh interval watching the new token's expiry.
      // If it fails the reactive 401 handler takes over.
      clearInterval(refreshInterval!);
      refreshInterval = null;

      try {
        await refreshAuthorization(undefined);
      } catch (error) {
        console.warn("Proactive token refresh failed:", error);
      }
    }
  }, 1000);
};

// Cleanup function to clear the refresh interval
export const clearTokenRefreshTimer = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

const resetSession = (to = "/#/welcome") => {
  // Clear refresh timer
  clearTokenRefreshTimer();

  localStorage.clear();
  window.location.assign(to);
};
// Function that will be called to refresh authorization
export const refreshAuthorization = async (failedRequest: any) => {
  const refreshToken = store.get(refreshTokenAtom);

  if (!refreshToken) {
    // If no refresh token, try to refresh public token (for login flows)
    try {
      await ensurePublicTokenIsFresh();
      const newToken = store.get(accessTokenAtom);
      if (newToken && failedRequest?.response?.config?.headers) {
        failedRequest.response.config.headers["Authorization"] =
          `Bearer ${newToken}`;
      }

      return Promise.resolve();
    } catch (error) {
      // If public token refresh fails, show session expired modal
      withScope(function (scope) {
        scope.setTag("section", "portal");
        scope.setTag("domain", "refreshAuthorization");
        scope.setTag("endpoint", "refreshAuthorization");
        scope.setExtra("refreshToken", refreshToken);
        captureMessage("Session expired - missing refresh token");
      });
      store.set(showsSessionExpiredModalAtom, true);

      return Promise.reject(new Error("Session expired"));
    }
  }

  if (refreshToken === anonymousLoginStatus) {
    // For anonymous/public token, refresh it instead of redirecting
    // This allows login flows to continue when the public token expires
    try {
      await ensurePublicTokenIsFresh();
      const newToken = store.get(accessTokenAtom);

      if (newToken && failedRequest?.response?.config?.headers) {
        failedRequest.response.config.headers["Authorization"] =
          `Bearer ${newToken}`;
      }

      return Promise.resolve();
    } catch (error) {
      // If public token refresh fails, redirect to welcome page
      resetSession("/#/welcome");

      return Promise.reject(error);
    }
  } else if (refreshToken === agentLoginStatus) {
    resetSession();

    return;
  }

  const url = new URL(TOKEN_URL);
  url.searchParams.append("grant_type", "refresh_token");
  url.searchParams.append("refresh_token", refreshToken);
  url.searchParams.append("client_id", env.clientAppId);

  // Deduplicate concurrent refresh attempts — e.g. the proactive interval and
  // a reactive 401 from axios-auth-refresh firing at the same moment would
  // otherwise each send a POST /token, burning a single-use refresh token.
  // All concurrent callers share the same in-flight HTTP call via this promise.
  if (!refreshAuthorizationPromise) {
    refreshAuthorizationPromise = axios
      .post(url.toString())
      .then((response: AxiosResponse<CompaxOAuth2AccessTokenModel>) => {
        store.set(accessTokenAtom, response.data.access_token!);
        // Only update refresh token if a new one is provided
        if (response.data.refresh_token) {
          store.set(refreshTokenAtom, response.data.refresh_token);
        }

        // Schedule proactive refresh for the new token
        scheduleTokenRefresh();
      })
      .catch((error) => {
        if (!store.get(showsSessionExpiredModalAtom)) {
          if (
            // Handle invalid refresh token error
            error?.response?.data?.error === "invalid_token" ||
            (typeof error?.response?.data?.error_description === "string" &&
              error.response.data.error_description.includes(
                "Invalid refresh token",
              ))
          ) {
            // For invalid refresh token, show the modal instead of resetting session
            store.set(showsSessionExpiredModalAtom, true);
          }
        }

        return Promise.reject(error);
      })
      .finally(() => {
        // Clear so a subsequent call after a failure can start a fresh refresh.
        refreshAuthorizationPromise = null;
      });
  }

  // Each caller awaits the shared promise, then updates its own request's
  // Authorization header independently with the freshly stored token.
  return refreshAuthorizationPromise.then(() => {
    const newToken = store.get(accessTokenAtom);

    if (newToken && failedRequest?.response?.config?.headers) {
      failedRequest.response.config.headers["Authorization"] =
        `Bearer ${newToken}`;
    }
  });
};
